import { useCallback } from 'react';
import { useCurrentUser } from 'api/hooks/useCurrentUser';
import useKeystoreStorageHelpers from 'utils/helpers/keystoreStorage.helpers';
import {
  getCachedPinKey,
  isPinKeyNotFoundError,
  removeCachedPinKey,
  runKeystoreMutation,
  setCachedPinKey,
} from 'utils/helpers/pinKeyCache.helpers';
import { clearSessionWrapKey } from 'utils/helpers/pinSessionKey.helpers';
import { usePinOrgId } from './usePinOrgId';

export const useDeletePin = () => {
  const { data: user } = useCurrentUser();
  const orgId = usePinOrgId();
  const { constructPinKeyPath, deletePinKey } = useKeystoreStorageHelpers();

  // signed in: remove the user's own pin.key and local copy
  const deletePin = useCallback(async (): Promise<void> => {
    if (!user || !orgId) return;

    const pinKeyPath = constructPinKeyPath({
      tenantId: user.tenant.id,
      locationId: user.location.locationId,
      userId: user.userId,
    });
    // hide the PIN and stop rewrapping before the DELETE, so a refresh during it can't queue an upload
    const entry = getCachedPinKey(orgId, user.email);
    if (entry) setCachedPinKey(orgId, user.email, { ...entry, pendingUpload: false, pendingDelete: true });
    await clearSessionWrapKey();
    try {
      await runKeystoreMutation(pinKeyPath, () => deletePinKey(pinKeyPath));
    } catch (error) {
      if (!isPinKeyNotFoundError(error)) throw error;
    }
    removeCachedPinKey(orgId, user.email);
  }, [user, orgId, constructPinKeyPath, deletePinKey]);

  // logged out: a pin.key can only be deleted by its owner, so hide it now and delete it after login
  const forgetPin = useCallback(
    (email: string): void => {
      const entry = orgId ? getCachedPinKey(orgId, email) : undefined;
      if (orgId && entry) setCachedPinKey(orgId, email, { ...entry, pendingUpload: false, pendingDelete: true });
    },
    [orgId],
  );

  return { deletePin, forgetPin };
};
